import React from 'react';
import { BarChart3, ThumbsUp } from 'lucide-react';
import { Post } from '../../types';

interface PollDisplayProps {
  post: Post;
  onVote: (optionIndex: number) => void;
}

const PollDisplay: React.FC<PollDisplayProps> = ({ post, onVote }) => {
  const totalVotes = post.pollVotesCount 
    ? post.pollVotesCount.reduce((sum, count) => sum + count, 0) 
    : 0;
  
  return (
    <div className="space-y-2">
      <h4 className="font-medium text-gray-900 dark:text-gray-100 flex items-center space-x-2">
        <BarChart3 size={16} className="text-blue-600 dark:text-blue-400" />
        <span>Poll</span>
        <span className="text-sm text-gray-500 dark:text-gray-400">({totalVotes} votes)</span>
      </h4>
      
      <div className="space-y-2">
        {post.pollOptions?.map((option, index) => {
          const voteCount = post.pollVotesCount?.[index] || 0;
          const votePercentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
          const isSelected = post.userVote === index;
          
          return (
            <button
              key={index}
              onClick={() => onVote(index)}
              disabled={post.userVote !== undefined}
              className={`w-full p-3 rounded-lg text-left relative overflow-hidden transition-all duration-200 ${
                isSelected 
                  ? 'bg-green-100 dark:bg-green-900/50 border border-green-300 dark:border-green-700' 
                  : post.userVote !== undefined
                  ? 'bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                  : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750'
              }`}
            >
              {/* Background progress bar */}
              <div 
                className={`absolute top-0 left-0 bottom-0 ${
                  isSelected ? 'bg-green-200 dark:bg-green-800/50' : 'bg-gray-200 dark:bg-gray-700/50'
                }`}
                style={{ width: `${votePercentage}%` }}
              />
              
              {/* Option content */}
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    isSelected 
                      ? 'bg-green-500 text-white' 
                      : 'border border-gray-300 dark:border-gray-600'
                  }`}>
                    {isSelected && <ThumbsUp size={12} />}
                  </div>
                  <span className="dark:text-gray-200">{option}</span>
                </div>
                <span className="font-medium dark:text-gray-200">{votePercentage}%</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PollDisplay;